import { queryClient, queryKeys } from '@/lib/query-client';
import { useMutation, useQuery } from '@tanstack/react-query';
import { plannerService } from '../api/plannerService';
import type {
  CreatePlannerEventBody,
  PlannerEvent,
  UpdatePlannerEventBody,
  ValidatePlannerEventBody,
} from '../types/planner';

export function usePlannerResources(teamId?: number) {
  return useQuery({
    queryKey: queryKeys.planner.resources(teamId),
    queryFn: () => plannerService.getResources(teamId),
    staleTime: 5 * 60 * 1000,
  });
}

export function usePlannerEvents(
  start: string,
  end: string,
  teamId?: number,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.planner.events(start, end, teamId),
    queryFn: () => plannerService.getEvents(start, end, teamId),
    enabled: !!start && !!end && (options?.enabled ?? true),
    staleTime: 30 * 1000,
  });
}

function invalidatePlannerEvents() {
  queryClient.invalidateQueries({ queryKey: queryKeys.planner.all });
}

export function useCreatePlannerEvent() {
  return useMutation({
    mutationFn: (data: CreatePlannerEventBody) => plannerService.createEvent(data),
    onSuccess: invalidatePlannerEvents,
    meta: {
      successTitle: 'Event Scheduled',
      successMessage: 'The event has been added to the calendar',
    },
  });
}

export function useUpdatePlannerEvent() {
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdatePlannerEventBody }) =>
      plannerService.updateEvent(id, data),
    // Optimistic update: immediately apply changes in cache
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.planner.all });
      const snapshots = queryClient.getQueriesData<PlannerEvent[]>({
        queryKey: queryKeys.planner.all,
      });
      queryClient.setQueriesData<PlannerEvent[]>({ queryKey: queryKeys.planner.all }, (old) => {
        if (!old) return old;
        return old.map((e) =>
          e.id === id
            ? {
                ...e,
                ...(data.start && { start: data.start }),
                ...(data.end && { end: data.end }),
                ...(data.userId && { userId: data.userId }),
              }
            : e,
        );
      });
      return { snapshots };
    },
    // Rollback on error
    onError: (_err, _vars, context) => {
      if (context?.snapshots) {
        for (const [queryKey, data] of context.snapshots) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },
    onSettled: invalidatePlannerEvents,
    meta: {
      successTitle: 'Event Updated',
      successMessage: 'Schedule changes saved successfully',
    },
  });
}

export function useDeletePlannerEvent() {
  return useMutation({
    mutationFn: (id: number) => plannerService.deleteEvent(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.planner.all });
      const snapshots = queryClient.getQueriesData<PlannerEvent[]>({
        queryKey: queryKeys.planner.all,
      });
      queryClient.setQueriesData<PlannerEvent[]>({ queryKey: queryKeys.planner.all }, (old) =>
        old?.filter((e) => e.id !== id),
      );
      return { snapshots };
    },
    onError: (_err, _vars, context) => {
      if (context?.snapshots) {
        for (const [queryKey, data] of context.snapshots) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },
    onSettled: invalidatePlannerEvents,
    meta: {
      successTitle: 'Event Deleted',
      successMessage: 'Event removed from calendar',
    },
  });
}

export function useValidatePlannerEvent() {
  return useMutation({
    mutationFn: (data: ValidatePlannerEventBody) => plannerService.validateEvent(data),
    meta: {
      successTitle: 'Event Validated',
      successMessage: 'Event validation completed successfully',
    },
  });
}
